/**
 * Tabs — in-page section switching.
 *
 * Used by: the product editor (thông tin / biến thể / tùy chọn / dị ứng /
 * station / giá theo chi nhánh), the translation screen (hàng đợi / từ điển /
 * nhà cung cấp / ngôn ngữ), settings, and the POS order panel.
 *
 * This is a CONTROLLED component with no routing opinion, so a screen can put
 * the active tab in the URL (`?tab=variants`) and keep it shareable.
 *
 * Accessibility: implements the WAI-ARIA tabs pattern — `role="tablist"`,
 * roving tabindex, ArrowLeft/ArrowRight/Home/End move between tabs, and each
 * panel is linked with `aria-controls`/`aria-labelledby`. Only the active tab
 * is in the tab order, which is what keyboard users expect.
 */
import { useRef } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../utils.js';

export interface TabItem {
  readonly id: string;
  readonly label: ReactNode;
  /** Small count/badge shown after the label. */
  readonly badge?: ReactNode;
  readonly disabled?: boolean;
}

export interface TabsProps {
  readonly items: readonly TabItem[];
  readonly value: string;
  readonly onChange: (id: string) => void;
  readonly variant?: 'underline' | 'pill';
  readonly className?: string;
  readonly ariaLabel?: string;
}

export function Tabs({
  items,
  value,
  onChange,
  variant = 'underline',
  className,
  ariaLabel = 'Mục',
}: TabsProps): React.JSX.Element {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const move = (delta: number): void => {
    const enabled = items.filter((i) => i.disabled !== true);
    const index = enabled.findIndex((i) => i.id === value);
    if (index < 0) return;
    const next = enabled[(index + delta + enabled.length) % enabled.length];
    if (next === undefined) return;
    onChange(next.id);
    refs.current[next.id]?.focus();
  };

  const jump = (position: 'first' | 'last'): void => {
    const enabled = items.filter((i) => i.disabled !== true);
    const target = position === 'first' ? enabled[0] : enabled[enabled.length - 1];
    if (target === undefined) return;
    onChange(target.id);
    refs.current[target.id]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          move(1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          move(-1);
        } else if (e.key === 'Home') {
          e.preventDefault();
          jump('first');
        } else if (e.key === 'End') {
          e.preventDefault();
          jump('last');
        }
      }}
      className={cn(
        'flex items-center gap-1 overflow-x-auto',
        variant === 'underline' && 'border-b border-line',
        variant === 'pill' && 'rounded-lg bg-surface-alt p-1',
        className,
      )}
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            ref={(node) => {
              refs.current[item.id] = node;
            }}
            type="button"
            role="tab"
            id={`tab-${item.id}`}
            aria-selected={active}
            aria-controls={`tabpanel-${item.id}`}
            tabIndex={active ? 0 : -1}
            disabled={item.disabled === true}
            onClick={() => onChange(item.id)}
            className={cn(
              'inline-flex min-h-touch shrink-0 items-center gap-2 whitespace-nowrap px-3 text-sm font-medium transition-colors',
              'outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50',
              variant === 'underline' &&
                (active
                  ? 'border-b-2 border-brand-600 text-brand-700'
                  : 'border-b-2 border-transparent text-fg-muted hover:text-fg'),
              variant === 'pill' &&
                (active ? 'rounded-md bg-surface text-fg shadow-card' : 'rounded-md text-fg-muted hover:text-fg'),
            )}
          >
            {item.label}
            {item.badge !== undefined ? (
              <span className="rounded-full bg-surface-sunken px-1.5 py-0.5 text-[11px] font-semibold text-fg-muted">
                {item.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Panel wrapper that supplies the ids `Tabs` points at. */
export function TabPanel({
  id,
  active,
  className,
  children,
}: {
  readonly id: string;
  readonly active: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}): React.JSX.Element | null {
  if (!active) return null;
  return (
    <div role="tabpanel" id={`tabpanel-${id}`} aria-labelledby={`tab-${id}`} tabIndex={0} className={className}>
      {children}
    </div>
  );
}
